import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, KeyRound, Copy, Check, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { use2FAVerify } from '../api/use2fa';
import { useAuthStore } from '../store/authStore';

export default function Auth2FAVerifyPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore(s => s.setAuth);
  const [code, setCode] = useState('');
  const [isRecovery, setIsRecovery] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const verifyMutation = use2FAVerify();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length < 1) return;
    setLoading(true);
    try {
      const data = await verifyMutation.mutateAsync({ code, isRecovery });
      // Response format: { success: true, data: { accessToken, user } }
      // Frontend api client already unwrapped the outer .data layer
      if (data && typeof data === 'object' && 'accessToken' in data && 'user' in data) {
        const response = data as { accessToken: string; user: { id: string; email: string; name: string; role: string; permissions?: Record<string, boolean> } };
        setAuth(response.accessToken, response.user);
        toast.success('Accesso completato');
        navigate('/dashboard');
      } else {
        throw new Error('Risposta server non valida');
      }
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Codice non valido');
      setCode('');
      inputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100dvh',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--color-bg)', padding: '1rem',
    }}>
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none',
        background: 'radial-gradient(ellipse 60% 50% at 50% 0%, oklch(0.45 0.07 195 / 0.12), transparent)',
      }} />
      <div style={{
        width: '100%', maxWidth: 400,
        display: 'flex', flexDirection: 'column', gap: '2rem',
        position: 'relative',
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14,
            background: 'var(--color-primary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Shield size={28} color="white" />
          </div>
          <div style={{ textAlign: 'center' }}>
            <h1 style={{
              fontFamily: 'Satoshi, system-ui, sans-serif', fontWeight: 700,
              fontSize: '1.75rem', color: 'var(--color-text)', marginBottom: '0.25rem',
            }}>Verifica 2FA</h1>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
              {isRecovery ? 'Inserisci un recovery code' : 'Inserisci il codice dalla tua app autenticatore'}
            </p>
          </div>
        </div>

        {/* Card */}
        <div style={{
          background: 'var(--glass-bg-card)',
          border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: 'var(--radius-container)',
          padding: '2rem', boxShadow: 'var(--shadow-lg)',
        }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
              <label htmlFor="otp" style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text-muted)' }}>
                {isRecovery ? 'Recovery Code' : 'Codice OTP'}
              </label>
              <input
                ref={inputRef}
                id="otp"
                type="text"
                value={code}
                onChange={e => {
                  if (isRecovery) {
                    setCode(e.target.value.toUpperCase());
                  } else {
                    setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                  }
                }}
                required
                autoFocus
                autoComplete="one-time-code"
                placeholder={isRecovery ? 'XXXXXXXX' : '000000'}
                style={{
                  background: 'var(--glass-bg-card)',
                  border: '1px solid rgba(255,255,255,0.10)',
                  borderRadius: 'var(--radius-card)',
                  color: 'var(--color-text)',
                  fontSize: isRecovery ? 'var(--text-lg)' : '2rem',
                  padding: '0.75rem 1rem',
                  width: '100%', outline: 'none',
                  textAlign: 'center', fontFamily: 'monospace',
                  letterSpacing: isRecovery ? '0.2em' : '0.5em',
                }}
                onFocus={e => (e.target.style.borderColor = 'var(--color-primary)')}
                onBlur={e => (e.target.style.borderColor = 'rgba(255,255,255,0.10)')}
              />
            </div>

            {verifyMutation.isError && (
              <div style={{
                background: 'var(--color-error-highlight)',
                border: '1px solid var(--color-error)',
                borderRadius: 'var(--radius-card)',
                padding: '0.625rem 0.875rem',
                fontSize: 'var(--text-sm)', color: 'var(--color-error)',
              }}>
                {(verifyMutation.error as Error)?.message || 'Codice non valido'}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || verifyMutation.isPending}
              style={{
                background: 'var(--color-primary)',
                border: 'none', borderRadius: 'var(--radius-card)',
                color: '#fff', fontSize: 'var(--text-base)', fontWeight: 600,
                padding: '0.75rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                transition: 'all var(--transition-interactive)',
              }}
              onMouseEnter={e => { if (!loading) (e.currentTarget.style.background = 'var(--color-primary-hover)'); }}
              onMouseLeave={e => { (e.currentTarget.style.background = 'var(--color-primary)'); }}
            >
              {loading ? 'Verifica in corso…' : 'Verifica'}
            </button>
          </form>

          {/* Toggle recovery/OTP */}
          <div style={{ marginTop: '1rem', textAlign: 'center' }}>
            <button
              onClick={() => { setIsRecovery(v => !v); setCode(''); inputRef.current?.focus(); }}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--color-primary)', fontSize: 'var(--text-sm)',
                display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
              }}
            >
              <KeyRound size={14} />
              {isRecovery ? 'Usa codice OTP' : 'Usa un recovery code'}
            </button>
          </div>
        </div>

        <p style={{ textAlign: 'center', fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>
          Il codice cambia ogni 30 secondi nella tua app autenticatore
        </p>
      </div>
    </div>
  );
}
